// @flow
import * as React from 'react';
import Modal from 'react-modal';

import { AdminUploadedFileDetailModal } from 'admin/components/AdminUploadedFileDetail';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import {
  ComposableTable,
  getErrorTextCellGenerator,
  getErrorCellGenerator,
  getTimestampCellGenerator,
  getLinkCellGenerator,
} from 'components/ComposableTable';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import Glyph from 'components/Glyph.jsx';
import Heading from 'components/Heading.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';

import type { AdminUploadedFiles_organizations } from './queries/__generated__/AdminUploadedFiles';

type Props = {
  organization: ?AdminUploadedFiles_organizations,
  loading: ?boolean,
  loadMoreFiles: (cursor: string) => void,
};

type State = {
  uploadedFileForModal: ?*,
  uploadedFileModalIsOpen: boolean,
};

export default class AdminUploadedFilesView extends React.Component<Props, State> {
  state = {
    uploadedFileForModal: undefined,
    uploadedFileModalIsOpen: false,
  };

  showUploadedFileModal = (uploadedFile: *) => {
    this.setState({ uploadedFileForModal: uploadedFile, uploadedFileModalIsOpen: true });
  };

  hideUploadedFileModal = () => {
    this.setState({ uploadedFileForModal: undefined, uploadedFileModalIsOpen: false });
  };

  formatDataIntoRow = (fileUpload: *) => {
    const { fileUrl, fileName, errors, uploadedAt, uuid } = fileUpload;
    const numberOfErrors = errors ? errors.length : 0;
    return {
      uuid,
      numberOfErrors,
      fileName,
      fileUrl,
      uploadedAt,
      onClick: numberOfErrors > 0 ? () => this.showUploadedFileModal(fileUpload) : undefined,
    };
  };

  render() {
    const { loading, organization } = this.props;
    const { uploadedFileModalIsOpen } = this.state;
    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (!organization) {
      return (
        <ErrorReporter error="AdminUploadedFilesView - tried to render without an organization" />
      );
    }

    const columnHeaders = ['CSV', 'Errors', 'Time received', ''].map(name => ({ name }));
    const columnGenerators = [
      getErrorTextCellGenerator('fileName', 'numberOfErrors'),
      getErrorCellGenerator('numberOfErrors'),
      getTimestampCellGenerator('uploadedAt'),
      getLinkCellGenerator('fileUrl', 'href', <Glyph solo size="medium" name="download" />),
    ];
    const rows = organization.autoMessageFileUploads.nodes.map(node =>
      this.formatDataIntoRow(node)
    );

    return (
      <React.Fragment>
        <Modal isOpen={uploadedFileModalIsOpen}>
          <AdminUploadedFileDetailModal
            onClose={this.hideUploadedFileModal}
            fileUpload={this.state.uploadedFileForModal}
          />
        </Modal>
        <Heading>Uploaded files</Heading>
        <Row size="small">
          <Text size="small" color="thunder">
            CSVs only available for download 90 days after time received.
          </Text>
        </Row>
        <ComposableTable
          columnHeaders={columnHeaders}
          columnGenerators={columnGenerators}
          rows={rows}
          keyField="uuid"
          rightAlignedLastColumn
        />
        {rows.length === 0 && (
          <Text align="center" element="div" size="small">
            <Row size="small">
              <Text weight="semibold">No files have been received yet</Text>
            </Row>
            <Text color="thunder">
              Files will appear here once they’re transferred to Remind via SFTP
            </Text>
          </Text>
        )}
      </React.Fragment>
    );
  }
}
